import { useCallback, useState } from "react";
import { createMaterial } from "@/lib/manufacturingClient";
import { parseMfgMsdsStatus, type MfgMsdsStatus } from "@/lib/manufacturing/msdsStatus";
import type { MfgMaterial } from "@/lib/manufacturing/types";

export type CreateMaterialPayload = {
  /** Optional. When omitted, the server auto-assigns a sequential code. */
  code?: string;
  name: string;
  unit: string;
  category: string;
  shipping_unit?: string;
  shipping_unit_weight?: number;
  pieces_per_shipping_unit?: number;
  /** Free-text primary vendor; persisted into `metadata.vendor`. */
  vendor?: string;
  /** VEN-2b: existing alias pick (wins over vendor string on save). */
  primary_alias_id?: string | null;
  /** MSDS-1: SDS requirement triage. */
  msds_status?: MfgMsdsStatus;
  /** Qty that triggers Fill from below-reorder (`metadata.reorder_threshold`). */
  reorder_threshold?: number | null;
  /** True when a $0 unit cost is intentional. */
  allow_zero_cost?: boolean;
};

export type CreateMaterialResult =
  | { ok: true }
  | { ok: false; error: string; duplicate?: MfgMaterial };

/**
 * Submit-side helper for the "+ New Material" modal hosted by the
 * Inventory panel. Returns the canonical `{ ok, error? }` shape
 * `ManufacturingPanelModal` expects on save, plus a toast string the
 * panel surfaces after a successful create. Toast clearing is the
 * caller's responsibility.
 *
 * On a 409 (duplicate name at this plant), the result includes the
 * existing material so the panel can re-open the modal in edit mode
 * against it instead of creating a second row.
 *
 * The toast surfaces the server-assigned code so the user can see what
 * their auto-assigned material was named without having to scroll the list.
 */
export function useManufacturingCreateMaterial(opts: { onCreated: () => void }) {
  const { onCreated } = opts;
  const [toast, setToast] = useState<string | null>(null);

  const submit = useCallback(
    async (payload: CreateMaterialPayload): Promise<CreateMaterialResult> => {
      const r = await createMaterial(payload);
      if (!r.ok) {
        const duplicate = extractDuplicate(r.body);
        if (duplicate) return { ok: false, error: r.error, duplicate };
        return { ok: false, error: r.error };
      }
      const assignedCode = r.data.material?.code ?? payload.code ?? "";
      setToast(
        assignedCode
          ? `Material ${assignedCode} (${payload.name}) created`
          : `Material "${payload.name}" created`
      );
      onCreated();
      return { ok: true };
    },
    [onCreated]
  );

  const dismissToast = useCallback(() => setToast(null), []);

  return { submit, toast, dismissToast };
}

function extractDuplicate(body: unknown): MfgMaterial | undefined {
  if (!body || typeof body !== "object") return undefined;
  const dup = (body as { duplicate?: unknown }).duplicate;
  if (!dup || typeof dup !== "object") return undefined;
  const d = dup as Record<string, unknown>;
  if (typeof d.id !== "string") return undefined;
  return {
    id: String(d.id),
    code: String(d.material_code ?? d.code ?? ""),
    name: String(d.name ?? ""),
    unit: String(d.unit ?? "each"),
    category: String(d.category ?? "general"),
    shipping_unit: String(d.shipping_unit ?? ""),
    shipping_unit_weight: Number(d.shipping_unit_weight) || 0,
    pieces_per_shipping_unit:
      Number(d.pieces_per_shipping_unit) > 0 ? Number(d.pieces_per_shipping_unit) : 1,
    msds_status: parseMfgMsdsStatus(d.msds_status),
    is_active: typeof d.is_active === "boolean" ? d.is_active : true,
    unit_cost: Number(d.unit_cost) || 0,
    on_hand_qty: Number(d.on_hand_qty) || 0,
    plant_id: String(d.plant_id ?? ""),
    metadata:
      d.metadata && typeof d.metadata === "object" ? (d.metadata as Record<string, unknown>) : {},
    created_at: String(d.created_at ?? ""),
    updated_at: String(d.updated_at ?? ""),
  };
}
